/**
 * The single place a step's kind is resolved (D-5): a Filter step behaves like
 * the drill kind it filters (`step.typePascalCase`).
 */
import type { StepTypeCamelCase } from 'src/types/course/step';
import { componentNameCamelCase } from 'shared/composables/strapi/useComponent';
import type { DrillKind } from './types';

export const DRILL_KINDS: readonly DrillKind[] = [
  'Vocabulary',
  'Pairs',
  'SingleChoice',
  'MultiChoice',
  'FillInTheBlank',
  'CrossMatch',
  'TrueFalse',
  'WordBuilder',
  'MemoryFlip',
  'SentenceBuilder',
];

/** The step fields the kind is read from (a `Step` model satisfies it). */
export interface KindSourceStep {
  step_type?: Partial<Array<{ __component?: string } | undefined>> | null;
  typePascalCase?: StepTypeCamelCase;
}

export function isDrillKind(value: unknown): value is DrillKind {
  return typeof value === 'string' && (DRILL_KINDS as string[]).includes(value);
}

/**
 * Exact port of useStep's `filterStepType`: the step_type component in
 * PascalCase ('Theory' for theory), a Filter step mapped to its real type.
 * The mapped value is not validated (same as today).
 */
export function resolveStepKind(
  step: KindSourceStep | null | undefined,
): StepTypeCamelCase | null | undefined {
  if (!step) return null;
  const component = step.step_type?.[0]?.__component ?? null;
  if (!component) return null;
  const camel = componentNameCamelCase(component) as StepTypeCamelCase;
  return camel === 'Filter' ? step.typePascalCase : camel;
}

/** The drill kind to run: Filter → its real kind; Theory/unknown → null. */
export function resolveDrillKind(
  step: KindSourceStep | null | undefined,
): DrillKind | null {
  const kind = resolveStepKind(step);
  return isDrillKind(kind) ? kind : null;
}
